"use client";

import React from "react";
import {PageCompetitionBookSchema} from "@/sanity/pages/PageCompetitionBookTemplate";

import Image from "next/image";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import DividerGreenLine from "@/components/global/ui/DividerGreenLine";
import ChipScrollingBanner from "@/components/global/ui/ChipScrollingBanner";
import InfoBox from "@/components/global/ui/InfoBox";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

import logo from "@/public/global/new-logo-full-light.svg";
import star from "@/public/competition-book/star.svg";
import calendar from "@/public/competition-book/calendar.svg";
import hero from "@/public/competition-book/hero.png";

export default function HeroSection({
  data,
  className,
}: {
  data: PageCompetitionBookSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative overflow-clip",
        className
      )}
    >
      <div className="px-[2rem] sm:px-[4rem] lg:px-[10rem] border-b border-b-white/10 hidden md:flex items-center justify-between py-[2rem] relative">
        <Image
          className="!w-[16rem]"
          src={logo}
          alt="Logo"
        />

        <div className="flex items-center gap-[4rem]">
          {data?.headerText1 && (
            <div className="flex items-center gap-[1rem]">
              <Image
                className="!w-[4.5rem]"
                src={star}
                alt="Star"
              />
              <p className="mb-0 leading-none text-white/60 text-[2.25rem]">{data.headerText1}</p>
            </div>
          )}

          {data?.headerText2 && (
            <div className="flex items-center gap-[1rem]">
              <Image
                className="!w-[4.5rem]"
                src={calendar}
                alt="Calendar"
              />
              <p className="mb-0 leading-none text-white/60 text-[2.25rem]">{data?.headerText2}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row-reverse items-center lg:items-start lg:justify-end gap-[2rem]">
        <div className="lg:-translate-x-[5.5rem] pt-[4rem] lg:pt-[17.75rem] w-[min(93.5rem,100%)] px-[2rem] lg:pl-0 flex flex-col items-center lg:items-start">
          {data?.heroChip && (
            <div className="border-l border-[#D9F9B9] bg-[#D9F9B9]/10 text-[#D9F9B9] px-[2rem] min-h-[5.375rem] flex items-center justify-center mb-[2rem]">
              {data.heroChip}
            </div>
          )}

          {data?.heroTitle && (
            <div className="richtext lg:richtext-start prose-headings:tracking-normal prose-headings:!font-ivy prose-headings:!text-[5rem] sm:prose-headings:!text-[8rem] lg:prose-headings:!text-[10rem] prose-headings:leading-none italic prose-headings:gradient-title-white prose-headings:pb-[3rem]">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </div>
          )}

          <DividerGreenLine
            className="w-[min(89.5rem,100%)] mb-[3rem]"
            greenLineClassName="lg:left-0 lg:translate-x-0"
          />

          {data?.heroText && (
            <div className="richtext lg:richtext-start prose-p:text-white/60 prose-p:font-light prose-strong:text-white prose-strong:font-normal">
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTACornerButton
              className="[&_button]:!text-[2rem] lg:[&_button]:!text-[2.25rem] mt-[3rem]"
              data={data.heroCTA}
            />
          )}

          {/* <InfoBox
            className="mt-[1rem] prose-p:text-[2.25rem] prose-p:font-normal w-auto prose-p:text-center"
            title={data.heroPreorder.text}
            color="#FFAC2F"
          /> */}
        </div>

        <div className="w-[calc(100%+14rem)] lg:w-[min(140.5rem,100%)] lg:mt-[9rem] flex-shrink-0 relative">
          <Image
            className="!w-full !max-w-none"
            src={hero}
            alt=""
          />

          {/* {data?.heroPreorder?.text && (
            <div className="absolute bottom-[25rem] left-1/2 -translate-x-1/2 lg:-translate-x-[65%] hidden md:flex items-center gap-[1.5rem] p-[1.5rem] rounded-[.5rem] w-[min(45rem,100%)] gradient-border-white-2 before:opacity-20 before:rotate-180 backdrop-blur-[2rem] shadow-[-3px_-3px_20px_0_rgba(255,255,255,0.15)_inset,3px_3px_8px_0_rgba(255,255,255,0.15)_inset]">
              {data?.heroPreorder?.icon && (
                <Image
                  className="!w-[5rem] flex-shrink-0"
                  src={data.heroPreorder.icon}
                  alt=""
                />
              )}

              <p className="mb-0 text-white leading-[1.2em]">{data.heroPreorder.text}</p>
            </div>
          )} */}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 w-full">
        <ChipScrollingBanner
          texts={data.heroChips}
          speed="fast"
          direction="backward"
        />
      </div>
    </section>
  );
}
